import { Text } from "@chakra-ui/react";
import { useEffect, useState, type ReactElement } from "react";
import { Trans, useTranslation } from "react-i18next";
import { type BuyerBidV2DetailDto } from "../../../../autogen/bff-api";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { verdictReopensDecision } from "./decidedBidEdit";
import { useBidQualificationDecision } from "./useBidQualificationDecision";
import { useBidRejectionDecision } from "./useBidRejectionDecision";

interface EditorControls {
  hold: () => void;
  release: () => void;
  discard: () => void;
}

export interface DecidedBidEditGate {
  guardVerdict: (next: boolean | null, apply: () => void) => void;
  modal: ReactElement;
}

export const useDecidedBidEditGate = (
  detail: BuyerBidV2DetailDto,
  editor: EditorControls,
): DecidedBidEditGate => {
  const { t } = useTranslation();
  const { show, modal } = useConfirmationModal();
  const { decision, decide } = useBidQualificationDecision(detail);
  const rejection = useBidRejectionDecision(detail);
  const [bypassed, setBypassed] = useState(false);

  const decided = rejection.isRejected ? "REJECTED" : decision ? "QUALIFIED" : null;
  const bidId = detail.bid.id;

  useEffect(() => setBypassed(false), [bidId]);
  useEffect(() => {
    if (decided === null) setBypassed(false);
  }, [decided]);

  const reopen = async () => {
    // clearQualification refuses a bid that is not SUBMITTED, so the rejection has to go first.
    if (rejection.isRejected && !(await rejection.revoke())) return false;
    if (decision) await decide(false);
    return true;
  };

  const guardVerdict = (next: boolean | null, apply: () => void) => {
    apply();
    if (bypassed || !verdictReopensDecision(decided, next)) return;

    editor.hold();
    show({
      title: String(t("Reopen the decision?")),
      description: (
        <Text>
          <Trans
            i18nKey="The qualification of this supplier is <bold>{{state}}</bold>. If you change the answer, the status goes back to not assessed. Do you want to continue?"
            values={{ state: decided === "REJECTED" ? t("Rejected") : t("Qualified") }}
            components={{ bold: <Text as="span" fontWeight="bold" /> }}
          />
        </Text>
      ),
      confirmButtonText: String(t("Yes")),
      cancelButtonText: String(t("No")),
      confirmButtonColor: "red",
      size: "lg",
      onConfirmed: async () => {
        if (await reopen()) {
          setBypassed(true);
          editor.release();
        } else {
          editor.discard();
        }
      },
      onCancelled: () => editor.discard(),
    });
  };

  return { guardVerdict, modal };
};
